import useCurrentEventInfo from '@contexts/useCurrentEventInfo';
import useCurrentRoom from '@contexts/useCurrentRoom';
import useNameTagsControl from '@contexts/useNameTagsControl';
import useNotificationService from '@contexts/useNotificationService';
import {
  useConnectionState,
  useLocalParticipant,
} from '@livekit/components-react';
import {
  useAppState,
  useAuthStore,
  useSafeContext,
} from '@sunrise-ui/primitives';
import { ConnectionState } from 'livekit-client';
import React, {
  createContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from 'react';
import { type UserJoinedStage } from '../services/NotificationService';

type RoomStageContextType = {
  isUserOnStage: boolean;
  setIsUserOnStage: (isOnStage: boolean) => void;
};

// @ts-expect-error -- don't use default, instead console error if component not used in appropriate context
const RoomStageContext = createContext<RoomStageContextType>();
RoomStageContext.displayName = 'RoomStageContext';

export const RoomStageProvider = ({ children }: PropsWithChildren) => {
  const { notificationService } = useNotificationService();
  const [isUserOnStage, setIsUserOnStage] = useState(false);
  const connectionState = useConnectionState();
  const { appState } = useAppState();
  const { setShowNameTags } = useNameTagsControl();
  const { currentUserId } = useAuthStore();
  const { currentRoomId } = useCurrentEventInfo();
  const { localParticipant } = useLocalParticipant();
  const { isHost } = useCurrentRoom();

  const toggleAV = async (enabled: boolean) =>
    Promise.all([
      localParticipant.setMicrophoneEnabled(enabled),
      localParticipant.setCameraEnabled(enabled),
    ]);

  useEffect(() => {
    if (notificationService) {
      const removeListener = notificationService.addListener(
        'UserJoinedStage',
        async (joinStage: UserJoinedStage) => {
          const isHost = joinStage.is_host;
          if (joinStage.room_id === currentRoomId) {
            setIsUserOnStage(joinStage.user_id === currentUserId);
            // eventually add fade to black for everyone here
            if (!isHost && joinStage.user_id === currentUserId) {
              // if it's the current user, they are being brought to stage
              await toggleAV(true);
            } else if (!isHost) {
              // simple catch for now to ensure only 2 people are ever on stage
              // may have separate websocket event for leave stage in future
              await toggleAV(false);
              setShowNameTags(true);
            }
          }
        },
      );

      return () => {
        removeListener();
      };
    }
  }, [notificationService, currentUserId, currentRoomId, toggleAV]);

  // if the user is on stage and the app comes to the foreground, enable AV
  // TODO: This is called at the start when it doesn't need to be. Need to investigate how we could work out that condition to guard against
  useEffect(() => {
    if (
      (isUserOnStage || isHost) &&
      appState === 'active' &&
      connectionState === ConnectionState.Connected
    ) {
      void toggleAV(true);
    }
  }, [isUserOnStage, appState, connectionState, isHost]);

  return (
    <RoomStageContext.Provider
      value={{
        isUserOnStage,
        setIsUserOnStage,
      }}
    >
      {children}
    </RoomStageContext.Provider>
  );
};

export default function useRoomStage() {
  return useSafeContext(RoomStageContext);
}
